import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ApplicationCredentialsModel} from '../../../../models/api/applicationCredentialsModel';
import {ApplicationModel} from '../../../../models/api/applicationModel';
import {ServerAuthenticationStrategiesEnum} from '../../../../models/api/serverModel';
import {ServerEditorStateActions} from '../../../editors/serverEditor/useServerEditorState';
import {AtomLine} from '../../../rules/ruleEditor/expressions/atomLine';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';
import {ApplicationCredentialsBasicInputs} from '../inputs/applicationCredentialsBasicInputs';
import {ApplicationCredentialsBearerInputs} from '../inputs/applicationCredentialsBearerInputs';
import {ApplicationCredentialsOAuthInputs} from '../inputs/applicationCredentialsOAuthInputs';

interface ApplicationCredentialsCardProps {
  installedApplication: ApplicationModel;
  credentials: ApplicationCredentialsModel;
  onUpdate: ServerEditorStateActions['onUpdateApplicationCredentials'];
  isReadOnly: boolean;
  serverOrigin: string;
}

export const ApplicationCredentialsCard: FC<ApplicationCredentialsCardProps> = (props) => {
  const theme = useUserTheme();
  if (props.credentials.type === ServerAuthenticationStrategiesEnum.NONE) {
    return null;
  }

  const title = (
    <AtomLine>
      <span>
        <Trans id="server-editor-credentials-title">Credentials</Trans>
      </span>
      <TooltipCoordinator
        iconColor={theme.greys.shade70}
        render={() => (
          <Trans id="application-credentials-tooltip">
            These credentials will be used to authenticate requests to this server for your Front instance.
          </Trans>
        )}
      />
    </AtomLine>
  );

  return (
    <RuleMultilineParameterCard title={title} icon={<Icon name="mention" />}>
      {renderCredentials(props)}
    </RuleMultilineParameterCard>
  );
};

function renderCredentials(props: ApplicationCredentialsCardProps) {
  const {installedApplication, credentials, onUpdate, isReadOnly, serverOrigin} = props;

  switch (credentials.type) {
    case ServerAuthenticationStrategiesEnum.BASIC:
      return (
        <ApplicationCredentialsBasicInputs
          credentials={credentials}
          onUpdate={onUpdate}
          isReadOnly={isReadOnly}
        />
      );

    case ServerAuthenticationStrategiesEnum.BEARER:
      return (
        <ApplicationCredentialsBearerInputs
          credentials={credentials}
          onUpdate={onUpdate}
          isReadOnly={isReadOnly}
        />
      );

    case ServerAuthenticationStrategiesEnum.OAUTH2:
      return (
        <ApplicationCredentialsOAuthInputs
          installedApplication={installedApplication}
          credentials={credentials}
          isReadOnly={isReadOnly}
          serverOrigin={serverOrigin}
        />
      );

    default:
      return null;
  }
}
